import { useCallback, useRef, useState, useEffect } from 'react';
import { TranscriptUtterance } from '../types';

interface UseAutoScrollProps {
  utterances: TranscriptUtterance[];
  currentTime: number | undefined;
  isPlaying: boolean | undefined;
}

interface UseAutoScrollReturn {
  transcriptContainerRef: React.RefObject<HTMLDivElement | null>;
  autoScrollEnabled: boolean;
  handleUserInteraction: () => void;
  updateAutoScroll: (time: number) => void;
}

export const useAutoScroll = ({
  utterances,
  currentTime,
  isPlaying,
}: UseAutoScrollProps): UseAutoScrollReturn => {
  const transcriptContainerRef = useRef<HTMLDivElement | null>(null);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const userInteractionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollAnimationRef = useRef<number | null>(null);

  // Use refs for frequently changing values to avoid unnecessary recreations
  const utterancesRef = useRef(utterances);
  const isPlayingRef = useRef(isPlaying);
  const autoScrollEnabledRef = useRef(autoScrollEnabled);

  // Update refs when props change
  useEffect(() => {
    utterancesRef.current = utterances;
  }, [utterances]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    autoScrollEnabledRef.current = autoScrollEnabled;
  }, [autoScrollEnabled]);

  const findCurrentWord = useCallback((time: number) => {
    for (const utterance of utterancesRef.current) {
      if (!utterance.transcriptWords) continue;

      for (const word of utterance.transcriptWords) {
        if (time >= word.startTime && time <= word.endTime) {
          return word;
        }
      }
    }
    return null;
  }, []);

  // Keep track of current animation target and state
  const animationStateRef = useRef({
    targetScrollTop: 0,
    currentScrollTop: 0,
    isAnimating: false,
    lastUpdateTime: 0,
  });

  // Track current time for special scroll cases
  const currentTimeRef = useRef(currentTime);

  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);

  // Fast 1-second animation for re-engagement and play/stop events
  const fastScrollToWord = useCallback((wordElement: Element) => {
    if (!transcriptContainerRef.current) return;

    const container = transcriptContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const elementRect = wordElement.getBoundingClientRect();

    // Calculate the target scroll position to center the word
    const targetScrollTop =
      container.scrollTop +
      elementRect.top -
      containerRect.top -
      containerRect.height / 2 +
      elementRect.height / 2;

    // Cancel any existing animation
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
    }
    animationStateRef.current.isAnimating = false;

    // Fast 1-second animation
    const startScrollTop = container.scrollTop;
    const distance = targetScrollTop - startScrollTop;
    const duration = 1000; // 1 second
    const startTime = performance.now();

    const easeOutCubic = (t: number): number => {
      return 1 - Math.pow(1 - t, 3);
    };

    const fastAnimation = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);

      container.scrollTop = startScrollTop + distance * easedProgress;

      if (progress < 1) {
        scrollAnimationRef.current = requestAnimationFrame(fastAnimation);
      } else {
        scrollAnimationRef.current = null;
        // After fast scroll, update animation state to current position
        animationStateRef.current.targetScrollTop = targetScrollTop;
        animationStateRef.current.currentScrollTop = targetScrollTop;
      }
    };

    scrollAnimationRef.current = requestAnimationFrame(fastAnimation);
  }, []);

  const smoothScrollToWord = useCallback((wordElement: Element) => {
    if (!transcriptContainerRef.current) return;

    const container = transcriptContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const elementRect = wordElement.getBoundingClientRect();

    // Calculate the target scroll position to center the word
    const newTargetScrollTop =
      container.scrollTop +
      elementRect.top -
      containerRect.top -
      containerRect.height / 2 +
      elementRect.height / 2;

    // Update the target smoothly
    animationStateRef.current.targetScrollTop = newTargetScrollTop;

    // Start animation if not already running
    if (!animationStateRef.current.isAnimating) {
      animationStateRef.current.isAnimating = true;
      animationStateRef.current.currentScrollTop = container.scrollTop;
      animationStateRef.current.lastUpdateTime = performance.now();

      const smoothAnimation = (currentTime: number) => {
        animationStateRef.current.lastUpdateTime = currentTime;

        // Use lerp (linear interpolation) with adaptive speed for smooth following
        const lerpSpeed = 0.08; // Smooth following speed
        const distance =
          animationStateRef.current.targetScrollTop -
          animationStateRef.current.currentScrollTop;

        // If we're close enough to target, snap to it
        if (Math.abs(distance) < 1) {
          animationStateRef.current.currentScrollTop =
            animationStateRef.current.targetScrollTop;
          container.scrollTop = animationStateRef.current.currentScrollTop;
          animationStateRef.current.isAnimating = false;
          scrollAnimationRef.current = null;
          return;
        }

        // Smooth interpolation towards target
        animationStateRef.current.currentScrollTop += distance * lerpSpeed;
        container.scrollTop = animationStateRef.current.currentScrollTop;

        // Continue animation
        scrollAnimationRef.current = requestAnimationFrame(smoothAnimation);
      };

      scrollAnimationRef.current = requestAnimationFrame(smoothAnimation);
    }
  }, []);

  const scrollToCurrentWord = useCallback(
    (wordId: string) => {
      if (!transcriptContainerRef.current) return;

      // Find the DOM element for this word
      const wordElement = transcriptContainerRef.current.querySelector(
        `[data-word-id="${wordId}"]`
      );

      if (wordElement) {
        // No throttling needed - the new animation system handles smooth updates
        smoothScrollToWord(wordElement);
      }
    },
    [smoothScrollToWord]
  );

  const handleUserInteraction = useCallback(() => {
    // Disable auto-scroll on user interaction
    setAutoScrollEnabled(false);

    // Cancel any ongoing scroll animation and reset state
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }
    animationStateRef.current.isAnimating = false;

    // Clear existing timeout
    if (userInteractionTimeoutRef.current) {
      clearTimeout(userInteractionTimeoutRef.current);
    }

    // Re-enable auto-scroll after 10 seconds of inactivity
    userInteractionTimeoutRef.current = setTimeout(() => {
      setAutoScrollEnabled(true);

      // When re-enabling, do a fast scroll to current playing word
      if (isPlayingRef.current && currentTimeRef.current) {
        const currentWord = findCurrentWord(currentTimeRef.current);
        if (currentWord && currentWord.id && transcriptContainerRef.current) {
          const wordElement = transcriptContainerRef.current.querySelector(
            `[data-word-id="${currentWord.id}"]`
          );
          if (wordElement) {
            fastScrollToWord(wordElement);
          }
        }
      }
    }, 10000);
  }, [findCurrentWord, fastScrollToWord]);

  // Use a ref to track current word ID to avoid state update issues
  const currentPlayingWordIdRef = useRef<string | null>(null);

  const updateAutoScroll = useCallback(
    (time: number) => {
      // Find current word and auto-scroll if enabled and playing
      if (autoScrollEnabledRef.current && isPlayingRef.current) {
        const currentWord = findCurrentWord(time);

        if (currentWord && currentWord.id) {
          const wordId = currentWord.id.toString();
          // Only scroll if the playing word has changed
          if (wordId !== currentPlayingWordIdRef.current) {
            currentPlayingWordIdRef.current = wordId;
            scrollToCurrentWord(wordId);
          }
        }
      }
    },
    [findCurrentWord, scrollToCurrentWord]
  );

  // Enable auto-scroll when playback starts, reset word tracking when stopped
  useEffect(() => {
    if (isPlaying) {
      setAutoScrollEnabled(true);
      // Don't scroll on audio start - user action (clicking word) indicates they're already positioned correctly
      // Smooth scrolling will take over immediately to follow playback
    } else {
      // When audio stops, scroll to the last played word
      const lastPlayedWordId = currentPlayingWordIdRef.current;
      if (lastPlayedWordId && transcriptContainerRef.current) {
        const wordElement = transcriptContainerRef.current.querySelector(
          `[data-word-id="${lastPlayedWordId}"]`
        );
        if (wordElement) {
          fastScrollToWord(wordElement);
        }
      }

      // Clear state after scrolling
      currentPlayingWordIdRef.current = null;
      // Stop any ongoing animation
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }
      animationStateRef.current.isAnimating = false;
    }
  }, [isPlaying, fastScrollToWord]);

  // Cleanup timeouts and animations on unmount
  useEffect(() => {
    const currentAnimationState = animationStateRef.current;
    return () => {
      if (userInteractionTimeoutRef.current) {
        clearTimeout(userInteractionTimeoutRef.current);
      }
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
      if (currentAnimationState) {
        currentAnimationState.isAnimating = false;
      }
    };
  }, []);

  return {
    transcriptContainerRef,
    autoScrollEnabled,
    handleUserInteraction,
    updateAutoScroll,
  };
};
